import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { UserInfo } from '../lib/auth'
import { fetchBackendUserInfo, UNAUTHORIZED_EVENT } from '../api/backend'
import { clearWorkspaceCache } from '../api/backend/workspace'
import { clearTokens } from '../lib/authTokens'
import { getBackendUrl } from '../lib/backendUrl'
import { clearCurrentSessionId } from '../lib/storage'

interface AuthContextValue {
  user: UserInfo | null
  isLoading: boolean
  isLoggingOut: boolean
  isAuthenticated: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

function clearAccessibleCookies(): void {
  if (typeof document === 'undefined') return

  const cookieNames = document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0]?.trim())
    .filter(Boolean)

  const hostnameParts = window.location.hostname.split('.').filter(Boolean)
  const domains = new Set<string | undefined>([undefined, window.location.hostname])

  for (let index = 0; index < hostnameParts.length - 1; index += 1) {
    domains.add(`.${hostnameParts.slice(index).join('.')}`)
  }

  const paths = ['/', window.location.pathname]

  cookieNames.forEach((name) => {
    domains.forEach((domain) => {
      paths.forEach((path) => {
        const domainPart = domain ? `;domain=${domain}` : ''
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainPart}`
      })
    })
  })
}

export function mapBackendSessionUser(session: Awaited<ReturnType<typeof fetchBackendUserInfo>>): UserInfo {
  return {
    sub: session.sub,
    name: session.name ?? session.fullname,
    email: session.email,
    picture: session.picture ?? session.avatar,
    avatar: session.avatar ?? session.picture,
    fullname: session.fullname ?? session.name,
    roles: session.roles,
  }
}

function resolvePostLogoutRedirectUri(user: UserInfo | null): string {
  const role = user?.roles?.[0]?.toUpperCase()
  switch (role) {
    case 'STUDENT':
      return `${window.location.origin}/logged-out?role=student`
    case 'TEACHER':
      return `${window.location.origin}/admin/logged-out?role=teacher`
    default:
      return `${window.location.origin}/admin/logged-out?role=admin`
  }
}

function isPublicLoggedOutOrLoginPath(pathname: string): boolean {
  return pathname === '/admin/login'
    || pathname === '/admin/logged-out'
    || pathname === '/login'
    || pathname === '/logged-out'
}

function isLoggedOutPath(pathname: string): boolean {
  return pathname === '/admin/logged-out' || pathname === '/logged-out'
}

const AUTH_BYPASS = (import.meta.env.VITE_AUTH_BYPASS as string) === 'true'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()

  const [isLoading, setIsLoading] = useState(true)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<UserInfo | null>(null)

  const resetAuthState = useCallback(() => {
    clearTokens()
    clearCurrentSessionId()
    clearWorkspaceCache()
    setIsAuthenticated(false)
    setUser(null)
  }, [])

  useEffect(() => {
    let cancelled = false

    if (isLoggedOutPath(location.pathname)) {
      resetAuthState()
      setIsLoading(false)
      return () => {
        cancelled = true
      }
    }

    fetchBackendUserInfo()
      .then((response) => {
        if (cancelled) return
        setUser(mapBackendSessionUser(response))
        setIsAuthenticated(true)
      })
      .catch(() => {
        if (cancelled) return
        resetAuthState()
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [location.pathname, resetAuthState])

  const login = useCallback(async () => {
    window.location.href = `${window.location.origin}/admin/login`
  }, [])

  const logout = useCallback(async () => {
    const redirectUri = resolvePostLogoutRedirectUri(user)
    setIsLoggingOut(true)
    resetAuthState()
    clearAccessibleCookies()

    const url = new URL(`${getBackendUrl()}/logout`)
    url.searchParams.set('redirect_uri', redirectUri)
    window.location.href = url.toString()
  }, [resetAuthState, user])

  const getAccessToken = useCallback((): string | null => {
    // Temporary compatibility shim for callers that still branch on truthiness.
    return isAuthenticated ? 'cookie-session' : null
  }, [isAuthenticated])

  useEffect(() => {
    const handleUnauthorized = () => {
      resetAuthState()
      if (!isPublicLoggedOutOrLoginPath(location.pathname)) {
        navigate('/admin/login')
      }
    }

    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
  }, [location.pathname, navigate, resetAuthState])

  useEffect(() => {
    if (isLoading || isLoggingOut) return
    if (
      !isAuthenticated
      && !location.pathname.startsWith('/auth/')
      && !isPublicLoggedOutOrLoginPath(location.pathname)
    ) {
      navigate('/admin/login', { replace: true })
    }
  }, [isLoading, isLoggingOut, isAuthenticated, location.pathname, navigate])

  const value = useMemo(
    () => ({ user, isLoading, isLoggingOut, isAuthenticated, login, logout, getAccessToken }),
    [user, isLoading, isLoggingOut, isAuthenticated, login, logout, getAccessToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function useAuth(): AuthContextValue {
  return useAuthContext()
}
